import {
  MiniMap,
  Panel,
  getNodesBounds,
  useReactFlow,
  useStoreApi,
  useViewport,
  type FitViewOptions,
  type XYPosition,
} from "@xyflow/react"
import { ChevronDown, Map, Network, Plus } from "lucide-react"
import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react"
import { createPortal } from "react-dom"
import { FlowMinimapNode } from "@/components/flow/flow-minimap-node"
import { useDropdownMenu } from "@/components/ui/dropdown-menu-position"
import { Button } from "@/components/ui/primitives"
import { useTranslation } from "@/hooks/use-translation"
import { arrangeFlowNodes, flowArrangeNodesFromRows } from "@/lib/flow-arrange"
import { pollAnimationFrames } from "@/lib/poll-animation-frames"
import { cn } from "@/lib/utils"
import type { FlowNodeRow, FlowOpenLayout } from "@/types/flow"

export const FLOW_MIN_ZOOM = 0.05
export const FLOW_MAX_ZOOM = 2

const MINIMAP_WIDTH = 240
const MINIMAP_HEIGHT = 150
const MOVE_DURATION_MS = 200
const CLICK_DRAG_THRESHOLD_PX = 4
/** Открытие ждёт строки доски и замер нод дольше, чем «Упорядочить» по кнопке (~2 с). */
const MOUNT_FIT_MAX_ATTEMPTS = 120
/** Сколько после открытия держим кадр, пока доска досчитывает размеры. */
const FIT_SETTLE_MS = 2000
/** Сколько кадров подряд границы не меняются, прежде чем вписывать. */
const FIT_STABLE_FRAMES = 3

const MENU_ITEM_CLASS =
  "flex w-full items-center px-3 py-2 text-left text-sm text-ui-text-primary hover:bg-ui-surface-sunken disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"

type FlowViewportControlsProps = {
  rows: FlowNodeRow[]
  selectedIds: number[]
  canEdit: boolean
  /**
   * Один раз после маунта — тот же кадр, что пункт «Вместить всё» при закрытой карте.
   * arrange сначала выполняет «Упорядочить доску» с сохранением (только с правом правки).
   */
  openLayout?: FlowOpenLayout
  onMoveNodes: (updates: { id: number; x: number; y: number; displayWidth?: number | null }[]) => void
  onPaneMenu: (clientX: number, clientY: number, worldX: number, worldY: number) => void
}

function fitPadding(mapOpen: boolean): NonNullable<FitViewOptions["padding"]> {
  // Карта и кнопки лежат поверх доски: снизу оставляем место, чтобы ноды не уехали под них.
  const bottomPx = (mapOpen ? MINIMAP_HEIGHT + 8 : 0) + 32 + 24
  return {
    top: "48px",
    right: "48px",
    left: "48px",
    bottom: `${bottomPx}px` as `${number}px`,
  }
}

function flowFitAllOptions(mapOpen: boolean, nodes?: { id: string }[]): FitViewOptions {
  return {
    ...(nodes ? { nodes } : {}),
    padding: fitPadding(mapOpen),
    minZoom: FLOW_MIN_ZOOM,
    maxZoom: FLOW_MAX_ZOOM,
    duration: MOVE_DURATION_MS,
  }
}

const MINIMAP_STYLE = {
  position: "relative",
  margin: 0,
  top: "auto",
  right: "auto",
  bottom: "auto",
  left: "auto",
  width: MINIMAP_WIDTH,
  height: MINIMAP_HEIGHT,
} as const

export function FlowViewportControls({
  rows,
  selectedIds,
  canEdit,
  openLayout,
  onMoveNodes,
  onPaneMenu,
}: FlowViewportControlsProps) {
  const { t } = useTranslation("flow")
  const { zoom } = useViewport()
  const { fitView, getNodes, getZoom, screenToFlowPosition, setCenter, zoomTo } = useReactFlow()
  const storeApi = useStoreApi()
  const [mapOpen, setMapOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [arrangeOpen, setArrangeOpen] = useState(false)
  const zoomRootRef = useRef<HTMLDivElement>(null)
  const zoomButtonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const arrangeRootRef = useRef<HTMLDivElement>(null)
  const arrangeButtonRef = useRef<HTMLButtonElement>(null)
  const arrangeMenuRef = useRef<HTMLDivElement>(null)
  const addButtonRef = useRef<HTMLButtonElement>(null)
  const pointerRef = useRef({ x: 0, y: 0, moved: false })
  const rowsRef = useRef(rows)
  const fittedRef = useRef(false)
  rowsRef.current = rows
  const arrangeOnOpenRef = useRef(openLayout === "arrange" && canEdit)
  arrangeOnOpenRef.current = openLayout === "arrange" && canEdit
  const canArrangeSelection = selectedIds.length >= 2
  const canArrangeBoard = rows.length >= 2

  const { menuStyle, portalRoot } = useDropdownMenu({
    open: menuOpen,
    direction: "up",
    align: "right",
    anchorRef: zoomButtonRef,
    menuRef,
    rootRef: zoomRootRef,
    resolveWidth: () => ({ minWidth: "10.5rem" }),
    onOutsideClick: () => setMenuOpen(false),
    estimatedMenuHeightPx: 88,
    estimatedMenuWidthPx: 168,
  })

  const arrangeMenu = useDropdownMenu({
    open: arrangeOpen,
    direction: "up",
    align: "right",
    anchorRef: arrangeButtonRef,
    menuRef: arrangeMenuRef,
    rootRef: arrangeRootRef,
    resolveWidth: () => ({ minWidth: "14rem" }),
    onOutsideClick: () => setArrangeOpen(false),
    estimatedMenuHeightPx: 88,
    estimatedMenuWidthPx: 224,
  })

  useEffect(() => {
    if (!menuOpen && !arrangeOpen) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      setMenuOpen(false)
      setArrangeOpen(false)
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [arrangeOpen, menuOpen])

  useEffect(() => {
    if (!canEdit) setArrangeOpen(false)
  }, [canEdit])

  // Вписать всё при открытии, когда доска перестала меняться: ноды доехали на места после
  // упорядочивания, шот дорисовал плеер, панель шота раскрылась. Пока идёт окно, кадр
  // догоняет новые границы; первое касание холста — колесо или нажатие — подгонку отключает.
  // Карта на маунте закрыта — те же отступы, что у пункта «Вместить всё».
  const fitWhenSettled = useCallback(() => {
    const dom = storeApi.getState().domNode
    let stopped = false
    let frame = 0
    const stop = () => {
      stopped = true
    }
    const signature = () => {
      const bounds = getNodesBounds(getNodes())
      return [bounds.x, bounds.y, bounds.width, bounds.height, dom?.clientWidth ?? 0, dom?.clientHeight ?? 0]
        .map((value) => Math.round(value))
        .join(":")
    }
    dom?.addEventListener("pointerdown", stop, true)
    dom?.addEventListener("wheel", stop, { capture: true, passive: true })
    const release = () => {
      cancelAnimationFrame(frame)
      dom?.removeEventListener("pointerdown", stop, true)
      dom?.removeEventListener("wheel", stop, true)
    }
    const until = performance.now() + FIT_SETTLE_MS
    let previous = ""
    let fitted = ""
    let stableFrames = 0
    const tick = () => {
      if (stopped || performance.now() > until) {
        release()
        return
      }
      const next = signature()
      stableFrames = next === previous ? stableFrames + 1 : 0
      previous = next
      if (stableFrames === FIT_STABLE_FRAMES && next !== fitted) {
        fitted = next
        void fitView(flowFitAllOptions(false))
      }
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => {
      stopped = true
      release()
    }
  }, [fitView, getNodes, storeApi])

  useEffect(() => {
    if (!openLayout || fittedRef.current) return
    let cancelSettled: (() => void) | undefined
    // Ждём строки доски (они приходят в сессию кадром позже маунта холста) и замер нод.
    const cancelMeasured = pollAnimationFrames(
      () => {
        if (fittedRef.current) return true
        const currentRows = rowsRef.current
        if (currentRows.length === 0) return false
        const ids = new Set(currentRows.map((row) => String(row.id)))
        const measured = getNodes().filter((node) => ids.has(node.id))
        if (measured.length !== currentRows.length || !measured.every((node) => node.measured?.width)) return false
        fittedRef.current = true
        if (arrangeOnOpenRef.current && currentRows.length >= 2) arrangeRowsRef.current(currentRows)
        cancelSettled = fitWhenSettled()
        return true
      },
      { maxAttempts: MOUNT_FIT_MAX_ATTEMPTS, startFrames: 2 },
    )
    return () => {
      cancelMeasured()
      cancelSettled?.()
    }
  }, [fitWhenSettled, getNodes, openLayout])

  const percentLabel = `${Math.round(zoom * 100)}%`

  const onMinimapPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    pointerRef.current = { x: event.clientX, y: event.clientY, moved: false }
  }

  const onMinimapPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const start = pointerRef.current
    if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > CLICK_DRAG_THRESHOLD_PX) {
      start.moved = true
    }
  }

  const onMinimapClick = (_event: unknown, position: XYPosition) => {
    if (pointerRef.current.moved) return
    void setCenter(position.x, position.y, { zoom: getZoom(), duration: MOVE_DURATION_MS })
  }

  const zoomTo100 = () => {
    setMenuOpen(false)
    void zoomTo(1, { duration: MOVE_DURATION_MS })
  }

  const zoomToFit = () => {
    setMenuOpen(false)
    void fitView(flowFitAllOptions(mapOpen))
  }

  const openAddMenu = () => {
    setMenuOpen(false)
    setArrangeOpen(false)
    const button = addButtonRef.current
    if (!button) return
    const rect = button.getBoundingClientRect()
    const pane = button.closest(".react-flow")?.getBoundingClientRect()
    const screen = pane
      ? { x: pane.left + pane.width / 2, y: pane.top + pane.height / 2 }
      : { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
    const world = screenToFlowPosition(screen)
    onPaneMenu(rect.left, rect.top, world.x, world.y)
  }

  // Раскладка и сохранение позиций; уже стоящие на месте ноды moveNodes не трогает.
  const arrangeRows = (source: FlowNodeRow[]) => {
    const boxes = flowArrangeNodesFromRows(source)
    const points = arrangeFlowNodes(boxes)
    if (points.length === 0) return points
    onMoveNodes(
      points.map((point) => ({
        id: point.id,
        x: point.x,
        y: point.y,
        displayWidth: boxes.find((box) => box.id === point.id)?.displayWidth ?? null,
      })),
    )
    return points
  }
  const arrangeRowsRef = useRef(arrangeRows)
  arrangeRowsRef.current = arrangeRows

  const arrange = (scope: "selection" | "board") => {
    setArrangeOpen(false)
    const source = scope === "selection" ? rows.filter((row) => selectedIds.includes(row.id)) : rows
    const points = arrangeRows(source)
    if (points.length === 0) return
    const nodes = points.map((point) => ({ id: String(point.id) }))
    // Позиции доезжают до React Flow эффектом после коммита rows, поэтому fit — следующим кадром.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        void fitView(flowFitAllOptions(mapOpen, nodes))
      })
    })
  }

  return (
    <Panel
      position="bottom-right"
      className="flex flex-col items-end gap-2"
      onPointerDown={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
    >
      {mapOpen ? (
        <div onPointerDown={onMinimapPointerDown} onPointerMove={onMinimapPointerMove}>
          <MiniMap
            pannable
            zoomable={false}
            ariaLabel={t("viewport.map")}
            bgColor="color-mix(in srgb, var(--ui-text-primary) 8%, var(--ui-bg-elevated))"
            maskColor="color-mix(in srgb, var(--ui-text-primary) 4%, transparent)"
            maskStrokeColor="var(--ui-accent)"
            maskStrokeWidth={2}
            nodeColor="color-mix(in srgb, var(--ui-text-primary) 22%, var(--ui-bg-elevated))"
            nodeComponent={FlowMinimapNode}
            nodeStrokeWidth={0}
            nodeBorderRadius={8}
            className="overflow-hidden rounded-lg border border-ui-border shadow-sm"
            style={MINIMAP_STYLE}
            onClick={onMinimapClick}
          />
        </div>
      ) : null}
      <div className="flex items-center gap-1">
        {canEdit ? (
          <>
            <Button
              ref={addButtonRef}
              type="button"
              variant="default"
              size="icon"
              aria-label={t("viewport.addNode")}
              className="size-8 border border-ui-accent/40 bg-ui-accent-soft text-ui-accent-text shadow-sm hover:bg-ui-accent/35 active:scale-100 active:bg-ui-accent/45 dark:shadow-sm"
              onClick={openAddMenu}
            >
              <Plus className="size-4" />
            </Button>
            <div ref={arrangeRootRef} className="inline-flex">
              <Button
                ref={arrangeButtonRef}
                type="button"
                variant="secondary"
                size="icon"
                aria-haspopup="menu"
                aria-expanded={arrangeOpen}
                aria-label={t("viewport.arrange")}
                className="size-8 shadow-sm"
                onClick={() => {
                  setMenuOpen(false)
                  setArrangeOpen((open) => !open)
                }}
              >
                <Network className="size-4" />
              </Button>
            </div>
          </>
        ) : null}
        <Button
          type="button"
          variant="secondary"
          size="icon"
          aria-pressed={mapOpen}
          aria-label={mapOpen ? t("viewport.hideMap") : t("viewport.showMap")}
          className={cn("size-8 shadow-sm", mapOpen && "bg-ui-surface-sunken")}
          onClick={() => setMapOpen((open) => !open)}
        >
          <Map className="size-4" />
        </Button>
        <div ref={zoomRootRef} className="inline-flex">
          <Button
            ref={zoomButtonRef}
            type="button"
            variant="secondary"
            size="sm"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={t("viewport.zoomMenu", { percent: percentLabel })}
            className="h-8 gap-1 px-2.5 tabular-nums shadow-sm"
            onClick={() => {
              setArrangeOpen(false)
              setMenuOpen((open) => !open)
            }}
          >
            {percentLabel}
            <ChevronDown className={cn("size-3.5 transition-transform", menuOpen && "rotate-180")} />
          </Button>
        </div>
      </div>
      {menuOpen && menuStyle && portalRoot
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={menuStyle}
              className="overflow-hidden rounded-lg border border-ui-border bg-ui-bg-elevated py-1 shadow-xl shadow-black/40 dark:bg-zinc-800"
              onPointerDown={(event) => event.stopPropagation()}
            >
              <button type="button" role="menuitem" className={MENU_ITEM_CLASS} onClick={zoomTo100}>
                {t("viewport.zoom100")}
              </button>
              <button type="button" role="menuitem" className={MENU_ITEM_CLASS} onClick={zoomToFit}>
                {t("viewport.zoomFit")}
              </button>
            </div>,
            portalRoot,
          )
        : null}
      {arrangeOpen && arrangeMenu.menuStyle && arrangeMenu.portalRoot
        ? createPortal(
            <div
              ref={arrangeMenuRef}
              role="menu"
              style={arrangeMenu.menuStyle}
              className="overflow-hidden rounded-lg border border-ui-border bg-ui-bg-elevated py-1 shadow-xl shadow-black/40 dark:bg-zinc-800"
              onPointerDown={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                role="menuitem"
                className={MENU_ITEM_CLASS}
                disabled={!canArrangeSelection}
                onClick={() => arrange("selection")}
              >
                {t("viewport.arrangeSelection")}
              </button>
              <button
                type="button"
                role="menuitem"
                className={MENU_ITEM_CLASS}
                disabled={!canArrangeBoard}
                onClick={() => arrange("board")}
              >
                {t("viewport.arrangeBoard")}
              </button>
            </div>,
            arrangeMenu.portalRoot,
          )
        : null}
    </Panel>
  )
}
